import React from 'react';
import { Button } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { CFT152, FT153 } from '../../../services/pwt205Api';

interface Tab2Props {
  topicCount: number;
  topics: Partial<CFT152>[];
  trackings: Partial<FT153>[];
  isEditing: boolean;
  onAddTopic: () => void;
  onDeleteTopic: (idx: number) => void;
  onUpdateTopic: (idx: number, key: keyof CFT152, value: unknown) => void;
  onAddTracking: (topicSeq: number) => void;
  onDeleteTracking: (topicSeq: number, trkIdx: number) => void;
  onUpdateTracking: (topicSeq: number, trkIdx: number, key: keyof FT153, value: unknown) => void;
}

const Tab2Topics: React.FC<Tab2Props> = React.memo(
  ({
    topicCount,
    topics,
    trackings,
    isEditing,
    onAddTopic,
    onDeleteTopic,
    onUpdateTopic,
    onAddTracking,
    onDeleteTracking,
    onUpdateTracking,
  }) => {
    return (
      <div className="pwt205-tab-content">
        {/* ft150_19 會議事項 (readOnly) */}
        <div className="form-grid" style={{ marginBottom: '8px' }}>
          <label>會議事項</label>
          <input value={String(topicCount ?? 0)} readOnly disabled style={{ width: '60px' }} />
        </div>

        <div className="section-header">
          <strong>議題清單</strong>
          {isEditing && (
            <Button size="small" icon={<PlusOutlined />} onClick={onAddTopic}>
              明細新增
            </Button>
          )}
        </div>

        {topics.map((topic, topicIdx) => {
          const topicSeq = topic.ft152_02 ?? topicIdx + 1;
          const topicTrackings = trackings.filter((t) => t.ft153_02 === topicSeq);

          return (
            <div key={topicIdx} className="topic-block">
              <div className="topic-header">
                <strong>議題 {topicSeq}</strong>
                {isEditing && (
                  <Button
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => onDeleteTopic(topicIdx)}
                  >
                    刪除議題
                  </Button>
                )}
              </div>
              <div className="form-grid">
                <label>議題內容</label>
                <textarea
                  value={topic.ft152_11 ?? ''}
                  onChange={(e) => onUpdateTopic(topicIdx, 'ft152_11', e.target.value)}
                  disabled={!isEditing}
                  rows={3}
                />
                <label>決議事項</label>
                <textarea
                  value={topic.ft152_12 ?? ''}
                  onChange={(e) => onUpdateTopic(topicIdx, 'ft152_12', e.target.value)}
                  disabled={!isEditing}
                  rows={3}
                />
              </div>

              {/* Tracking sub-table */}
              <div className="section-header" style={{ marginTop: '8px' }}>
                <span style={{ fontSize: '13px' }}>追蹤事項</span>
                {isEditing && (
                  <Button
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={() => onAddTracking(topicSeq)}
                  >
                    新增追蹤
                  </Button>
                )}
              </div>
              <table className="detail-table" style={{ fontSize: '12px' }}>
                <thead>
                  <tr>
                    <th>追蹤事項</th>
                    <th>預計完成</th>
                    <th>負責人員工號</th>
                    <th>負責人姓名</th>
                    <th>實際完成</th>
                    <th>處理說明</th>
                    {isEditing && <th>操作</th>}
                  </tr>
                </thead>
                <tbody>
                  {topicTrackings.map((trk, trkIdx) => (
                    <tr key={trkIdx}>
                      <td>
                        <input
                          value={trk.ft153_11 ?? ''}
                          onChange={(e) =>
                            onUpdateTracking(topicSeq, trkIdx, 'ft153_11', e.target.value)
                          }
                          disabled={!isEditing}
                          style={{ width: '120px' }}
                        />
                      </td>
                      <td>
                        <input
                          type="date"
                          value={trk.ft153_12 ? trk.ft153_12.substring(0, 10) : ''}
                          onChange={(e) =>
                            onUpdateTracking(
                              topicSeq,
                              trkIdx,
                              'ft153_12',
                              e.target.value ? e.target.value + 'T00:00:00Z' : null,
                            )
                          }
                          disabled={!isEditing}
                          style={{ width: '110px' }}
                        />
                      </td>
                      <td>
                        <input
                          value={trk.ft153_15 ?? ''}
                          onChange={(e) =>
                            onUpdateTracking(topicSeq, trkIdx, 'ft153_15', e.target.value)
                          }
                          disabled={!isEditing}
                          style={{ width: '70px' }}
                        />
                      </td>
                      <td>
                        <input
                          value={trk.ft153_15a ?? ''}
                          onChange={(e) =>
                            onUpdateTracking(topicSeq, trkIdx, 'ft153_15a', e.target.value)
                          }
                          disabled={!isEditing}
                          style={{ width: '70px' }}
                        />
                      </td>
                      <td>
                        <input
                          type="date"
                          value={trk.ft153_14 ? trk.ft153_14.substring(0, 10) : ''}
                          onChange={(e) =>
                            onUpdateTracking(
                              topicSeq,
                              trkIdx,
                              'ft153_14',
                              e.target.value ? e.target.value + 'T00:00:00Z' : null,
                            )
                          }
                          disabled={!isEditing}
                          style={{ width: '110px' }}
                        />
                      </td>
                      <td>
                        <input
                          value={trk.ft153_13 ?? ''}
                          onChange={(e) =>
                            onUpdateTracking(topicSeq, trkIdx, 'ft153_13', e.target.value)
                          }
                          disabled={!isEditing}
                          style={{ width: '100px' }}
                        />
                      </td>
                      {isEditing && (
                        <td>
                          <Button
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => onDeleteTracking(topicSeq, trkIdx)}
                          />
                        </td>
                      )}
                    </tr>
                  ))}
                  {topicTrackings.length === 0 && (
                    <tr>
                      <td
                        colSpan={isEditing ? 7 : 6}
                        style={{ textAlign: 'center', color: '#999' }}
                      >
                        尚無追蹤事項
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          );
        })}

        {topics.length === 0 && (
          <p style={{ textAlign: 'center', color: '#999' }}>尚無議題</p>
        )}
      </div>
    );
  },
);

Tab2Topics.displayName = 'Tab2Topics';

export default Tab2Topics;
